import React, { useEffect, useRef, useState } from 'react';
import { FaMicrophone, FaPause, FaTrash, FaUpload } from 'react-icons/fa';

function AudioRecorder({
  questionId,
  questionTypeId,
  disabled,
  preAssignedUrl,
  uploadAudio,
  onUploaded,
  onDelete,
  onUploadStart,
  onUploadEnd,
  onRequestStartRecording,
  onRecordingStarted,
  onRecordingStopped
}) {
  const timerInterval = useRef(null);
  const [timerValue, setTimerValue] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(preAssignedUrl || null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [chance1, setChance1] = useState(false);
  const [chance2, setChance2] = useState(false);
  const [mp3ApiMessageStage, setMp3ApiMessageStage] = useState(null);
  const [netDown, setNetDown] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userDismissedSuccessUi, setUserDismissedSuccessUi] = useState(false);
  const connectionCheckInterval = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const isUploadingRef = useRef(false);
  const mimeTypeRef = useRef(null);
  const fileInputRef = useRef(null);
  /** Skip applying blob from recorder.onstop when user cancels via trash during capture */
  const discardRecordingRef = useRef(false);

  const questionIdRef = useRef(questionId);
  questionIdRef.current = questionId;
  const onRecordingStoppedRef = useRef(onRecordingStopped);
  onRecordingStoppedRef.current = onRecordingStopped;
  const onUploadEndRef = useRef(onUploadEnd);
  onUploadEndRef.current = onUploadEnd;

  useEffect(() => {
    if (!preAssignedUrl) {
      setUserDismissedSuccessUi(false);
    }
  }, [preAssignedUrl]);

  useEffect(() => {
    if (preAssignedUrl) {
      setAudioUrl(preAssignedUrl);
      return;
    }
    // No server URL: don't clear local blob / preview while mic is active or we have a pending blob
    if (isRecording) return;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') return;
    setAudioUrl(prev => {
      if (prev && String(prev).startsWith('blob:')) return prev;
      return null;
    });
  }, [preAssignedUrl, isRecording]);

  const formatTime = seconds => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return [hrs.toString().padStart(2, '0'), mins.toString().padStart(2, '0'), secs.toString().padStart(2, '0')].join(':');
  };

  const abortRecordingCapture = () => {
    discardRecordingRef.current = true;
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
    } catch (e) {
      /* ignore */
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setIsPaused(false);
    setTimerValue(0);
    if (onRecordingStopped) onRecordingStopped(questionId);
  };

  const startRecording = async () => {
    if (typeof onRequestStartRecording === 'function') {
      const ok = onRequestStartRecording(questionId);
      if (!ok) return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      const mimeType = isIOS
        ? 'audio/mp4'
        : window.MediaRecorder && MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/ogg';

      mimeTypeRef.current = mimeType;
      discardRecordingRef.current = false;

      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];

      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          return;
        }
        const blob = new Blob(chunks, { type: mimeTypeRef.current || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
      };

      recorder.start();
      recorderRef.current = recorder;
      streamRef.current = stream;

      setIsRecording(true);
      setIsPaused(false);
      setTimerValue(0);
      setUploadSuccess(false);
      setErrorMessage('');

      if (onRecordingStarted) onRecordingStarted(questionId);

      timerInterval.current = setInterval(() => {
        setTimerValue(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic error:', err);
      alert('Mic permission denied or error occurred.');
    }
  };

  const togglePause = () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    if (isPaused) {
      recorder.resume();
      setIsPaused(false);
      timerInterval.current = setInterval(() => {
        setTimerValue(prev => prev + 1);
      }, 1000);
    } else {
      recorder.pause();
      setIsPaused(true);
      clearInterval(timerInterval.current);
    }
  };

  const stopRecording = () => {
    return new Promise(resolve => {
      const recorder = recorderRef.current;
      const stream = streamRef.current;
      if (recorder) {
        recorder.ondataavailable = e => {
          const blob = e.data;
          setAudioBlob(blob);
          resolve(blob);
        };
        recorder.stop();
        if (stream) stream.getTracks().forEach(track => track.stop());
        clearInterval(timerInterval.current);
        timerInterval.current = null;
        setIsRecording(false);
        recorderRef.current = null;
        streamRef.current = null;
        // Do not call onRecordingStopped here — user is proceeding to upload; parent keeps
        // "in progress" until onUploadEnd / abort. Abort path uses abortRecordingCapture → onRecordingStopped.
      } else {
        resolve(null);
      }
    });
  };

  const resetUploadedLocalState = () => {
    setAudioUrl(prev => {
      if (prev && String(prev).startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          /* ignore */
        }
      }
      return null;
    });
    setAudioBlob(null);
    setUploadSuccess(false);
    setErrorMessage('');
    setTimerValue(0);
    setMp3ApiMessageStage(null);
  };

  const handleTrash = () => {
    // const connection = navigator.connection;
    // const connectionLooksDown =
    //   Boolean(connection) && (connection.metered || (typeof connection.downlink === 'number' && connection.downlink < 1));
    if (!navigator.onLine) {
      alert('Network is down. Please try connecting to another network');
      return;
    }
    if (isRecording) {
      abortRecordingCapture();
      if (typeof onDelete === 'function') onDelete();
      return;
    }
    setUserDismissedSuccessUi(true);
    resetUploadedLocalState();
    const maybePromise = typeof onDelete === 'function' ? onDelete() : undefined;
    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.catch(() => {
        setUserDismissedSuccessUi(false);
      });
    }
  };

  const runAudioUpload = async blob => {
    setChance1(false);
    setChance2(false);
    setNetDown(false);
    setMp3ApiMessageStage(null);
    setUploadProgress(0);
    setErrorMessage('');
    let deferUploadCleanup = false;
    try {
      if (onUploadStart) onUploadStart(questionId);
      const onMp3RetryAttempt = ({ attempt }) => {
        setMp3ApiMessageStage(attempt);
      };
      await uploadAudio(blob, questionId, questionTypeId, setUploadProgress, onMp3RetryAttempt);
      setUploadSuccess(true);
      setTimerValue(0);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      if (onUploaded) onUploaded(url);
      setIsUploading(false);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadSuccess(false);
      if (error?.mp3AllRetriesFailed) {
        setMp3ApiMessageStage(error?.mp3FinalAttempt || 3);
        deferUploadCleanup = true;
        setTimeout(() => {
          setIsUploading(false);
          resetUploadedLocalState();
          if (onUploadEnd) onUploadEnd(questionId);
          isUploadingRef.current = false;
        }, 2500);
      } else {
        setIsUploading(false);
        resetUploadedLocalState();
      }
    } finally {
      if (!deferUploadCleanup) {
        if (onUploadEnd) onUploadEnd(questionId);
        isUploadingRef.current = false;
      }
    }
  };

  const handleSaveRecordingClick = async () => {
    if (isUploadingRef.current) return;
    // const connection = navigator.connection;
    // const connectionLooksDown =
    //   Boolean(connection) && (connection.metered || (typeof connection.downlink === 'number' && connection.downlink < 1));
    if (!navigator.onLine) {
      alert('Network is down. Please try connecting to another network');
      return;
    }
    isUploadingRef.current = true;
    setIsUploading(true);
    const blob = await stopRecording();
    if (!blob) {
      isUploadingRef.current = false;
      setIsUploading(false);
      return;
    }
    await runAudioUpload(blob);
  };

  const looksLikeAudioFile = file => {
    if (!file) return false;
    const typeOk =
      !file.type ||
      file.type.startsWith('audio/') ||
      file.type === 'application/octet-stream';
    const extOk = /\.(mp3|m4a|aac|wav|ogg|webm|mp4|mpeg|caf)(\?.*)?$/i.test(file.name || '');
    return typeOk || extOk;
  };

  const openAudioFilePicker = () => {
    if (!disabled || isUploadingRef.current || isRecording) return;
    if (typeof onRequestStartRecording === 'function') {
      const ok = onRequestStartRecording(questionId);
      if (!ok) return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleAudioFileSelected = async e => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (!looksLikeAudioFile(file)) {
      alert('Please select a valid audio file (mp3, m4a, wav, etc.).');
      return;
    }
    if (!navigator.onLine) {
      alert('Network is down. Please try connecting to another network');
      return;
    }
    if (isUploadingRef.current) return;
    isUploadingRef.current = true;
    setIsUploading(true);
    setAudioBlob(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    await runAudioUpload(file);
  };

  useEffect(() => {
    let attempts = 3;
    const checkConnection = async () => {
      const isConnected = navigator.onLine;
      if (!isConnected) {
        if (attempts === 3) {
          setChance1(true);
          setChance2(false);
          setNetDown(false);
        } else if (attempts === 2) {
          setChance1(false);
          setChance2(true);
          setNetDown(false);
        } else if (attempts === 1) {
          setChance1(false);
          setChance2(false);
          setNetDown(true);
        }
        if (attempts > 0) {
          attempts--;
          setTimeout(() => {
            if (isUploading) checkConnection();
          }, 5000);
        } else {
          setIsUploading(false);
          if (connectionCheckInterval.current) clearInterval(connectionCheckInterval.current);
          resetUploadedLocalState();
        }
      } else {
        setChance1(false);
        setChance2(false);
        setNetDown(false);
        attempts = 3;
      }
    };
    if (isUploading) {
      checkConnection();
      connectionCheckInterval.current = setInterval(checkConnection, 5000);
    }
    return () => {
      if (connectionCheckInterval.current) clearInterval(connectionCheckInterval.current);
    };
  }, [isUploading]);

  useEffect(() => {
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
      discardRecordingRef.current = true;
      try {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch (e) {
        /* ignore */
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      recorderRef.current = null;
      streamRef.current = null;
      // Parent keeps activeMp3RecordingQuestionIdRef / upload flags — clear on unmount so validation
      // matches UI after remount (e.g. form re-render when another answer changes).
      const qid = questionIdRef.current;
      if (typeof onRecordingStoppedRef.current === 'function') {
        onRecordingStoppedRef.current(qid);
      }
      if (isUploadingRef.current && typeof onUploadEndRef.current === 'function') {
        onUploadEndRef.current(qid);
      }
    };
  }, []);

  // While recording, never swap to "uploaded" UI (stale preAssignedUrl from parent refetch/merge).
  if (preAssignedUrl && !isRecording && !userDismissedSuccessUi) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <span style={{ color: '#9ebe21', fontWeight: '500', fontSize: '13px', display: 'flex', alignItems: 'center', padding: '4px 8px', borderRadius: '4px' }}>
            ✓ Audio uploaded successfully
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleTrash} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center' }}>
              <FaTrash />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isUploading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', border: '4px solid #f3f3f3', borderTop: '4px solid #9ebe21', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '14px', color: '#666' }}>
              {Math.round(uploadProgress * 100)}%
            </div>
          </div>
        </div>
        {mp3ApiMessageStage == null &&
          !netDown &&
          !chance1 &&
          !chance2 && (
          <div style={{ color: '#9ebe21', fontWeight: 500 }}>Audio uploading...</div>
        )}
        {mp3ApiMessageStage != null ? (
          <>
            {mp3ApiMessageStage === 1 && (
              <div style={{ color: 'red', fontWeight: 500 }}>Your connection is down...</div>
            )}
            {mp3ApiMessageStage === 2 && (
              <div style={{ color: 'red', fontWeight: 500 }}>It is taking longer than usual...</div>
            )}
            {mp3ApiMessageStage === 3 && (
              <div style={{ color: 'red', fontWeight: 500 }}>Audio upload failed...</div>
            )}
          </>
        ) : (
          <>
            {chance1 && <div style={{ color: 'red', fontWeight: 500 }}>Your connection is down...</div>}
            {chance2 && <div style={{ color: 'red', fontWeight: 500 }}>It is taking longer than usual...</div>}
            {netDown && <div style={{ color: 'red', fontWeight: 500 }}>Audio upload failed</div>}
          </>
        )}
        {errorMessage && <div style={{ color: 'red', fontSize: '13px', marginTop: '10px' }}>{errorMessage}</div>}
      </div>
    );
  }

  const actionButtonStyle = {
    background: '#fff',
    color: '#9ebe21',
    border: '2px solid #9ebe21',
    fontWeight: 'bold',
    borderRadius: '6px',
    padding: '12px 16px',
    minWidth: '72px',
    cursor: disabled ? 'pointer' : 'not-allowed',
    fontSize: '14px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*,audio/mpeg,audio/mp3,audio/mp4,audio/wav,audio/webm,audio/ogg,.mp3,.m4a,.wav"
        style={{ display: 'none' }}
        onChange={handleAudioFileSelected}
      />
      {!audioUrl && !isRecording && !uploadSuccess && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '28px',
            opacity: disabled ? 1 : 0.5,
            pointerEvents: disabled ? 'auto' : 'none',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button type="button" onClick={startRecording} style={actionButtonStyle} disabled={!disabled}>
              <FaMicrophone />
            </button>
            <p style={{ marginTop: '8px', marginBottom: 0 }}>Record</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button type="button" onClick={openAudioFilePicker} style={actionButtonStyle} disabled={!disabled}>
              <FaUpload />
            </button>
            <p style={{ marginTop: '8px', marginBottom: 0 }}>Upload</p>
          </div>
        </div>
      )}

      {isRecording && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ margin: '1rem 0' }}>
            <span>{formatTime(timerValue)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', position: 'relative' }}>
            <button type="button" onClick={handleTrash} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center', position: 'absolute', left: 0 }}>
              <FaTrash />
            </button>
            <button type="button" onClick={togglePause} style={{ background: '#fff', color: '#9ebe21', border: '2px solid #9ebe21', fontWeight: 'bold', borderRadius: '6px', padding: '12px 16px', cursor: 'pointer', fontSize: '14px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '8px', margin: '0 auto' }}>
              {isPaused ? <FaMicrophone /> : <FaPause />}
            </button>
            <button type="button" onClick={handleSaveRecordingClick} disabled={isUploading} style={{ background: isUploading ? '#ccc' : '#9ebe21', width: '52px', height: '36px', borderRadius: '10px', border: 'none', fontSize: '13px', color: '#fff', cursor: isUploading ? 'not-allowed' : 'pointer', position: 'absolute', right: 0 }}>
              {isUploading ? '...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {(audioUrl || uploadSuccess) && !isRecording && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
            <span style={{ color: '#9ebe21', fontWeight: '500', fontSize: '13px', display: 'flex', alignItems: 'center', padding: '4px 8px', borderRadius: '4px' }}>
              ✓ Audio uploaded successfully
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={handleTrash} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center' }}>
                <FaTrash />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AudioRecorder;


